import { css } from "@emotion/css";
import { Button, ErrorPanel, type ErrorInfo, Tooltip, Switch } from "@octopusdeploy/design-system-components";
import { CodeIcon, KubernetesIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { TaskState, ActivityElement, TaskResource, KubernetesObjectStatus, KubernetesResourceManifestSummaryResource } from "@octopusdeploy/octopus-server-client";
import type { ReactNode } from "react";
import React, { useEffect, useMemo, useState } from "react";
import { useAnalyticViewKubernetesAppliedManifestDispatch, useAnalyticViewKubernetesObjectDrawerDispatch } from "~/analytics/Analytics";
import type { StepWithKubernetesAction } from "~/areas/tasks/components/Task/K8sStatus/StepWithKubernetesAction";
import type { DiffSettings } from "~/areas/tasks/components/Task/Kubernetes/DiffConfigMenu";
import { DiffConfigMenu } from "~/areas/tasks/components/Task/Kubernetes/DiffConfigMenu";
import { KubernetesDetailsLoadingSkeleton } from "~/areas/tasks/components/Task/Kubernetes/KubernetesDetailsLoadingSkeleton";
import type { DeploymentDiffSelection, DiffQueryResult } from "~/areas/tasks/components/Task/Kubernetes/Manifests/KubernetesAppliedManifests";
import { KubernetesAppliedManifests } from "~/areas/tasks/components/Task/Kubernetes/Manifests/KubernetesAppliedManifests";
import { useTimelineItems } from "~/areas/tasks/components/Task/Kubernetes/Manifests/useTimelineItems";
import { KubernetesSnapshotStepsLayout } from "~/areas/tasks/components/Task/Kubernetes/Snapshot/KubernetesSnapshotStepsLayout";
import { createErrorInfo } from "~/areas/tasks/components/Task/Kubernetes/createErrorInfo";
import { flattenDiffsResourceHierarchy } from "~/areas/tasks/components/Task/Kubernetes/flattenDiffsResourceHierarchy";
import { getManifestSummaryForDeployment } from "~/areas/tasks/components/Task/Kubernetes/getManifestSummaryForDeployment";
import { useKubernetesManifestInspectionDiffs } from "~/areas/tasks/components/Task/Kubernetes/useKubernetesManifestInspectionDiffs";
import type { FilterSection } from "~/components/AdvancedFilterLayout/index";
import AdvancedFilterLayout from "~/components/AdvancedFilterLayout/index";
import type { DiffViewType } from "~/components/DiffViewer/DiffViewer";
import FilterSearchBox from "~/components/FilterSearchBox/index";
import useLocalStorage from "~/hooks/useLocalStorage";
import Select from "~/primitiveComponents/form/Select/Select";
import type { Item } from "~/primitiveComponents/form/Select/Select";
import StringHelper from "~/utils/StringHelper/index";
export type KubernetesDetailsBaseProps = {
    steps: StepWithKubernetesAction[];
    activityLogs: Pick<ActivityElement, "Children">[];
    projectId: string;
    snapshotId: string | undefined;
    environmentId: string;
    tenantId: string | undefined;
    taskState: TaskState;
};
type KubernetesDetailsProps = Omit<KubernetesDetailsBaseProps, "taskState"> & {
    activeView: KubernetesDetailsStepView;
    setActiveView: (view: KubernetesDetailsStepView) => void;
    task: TaskResource<{
        DeploymentId: string;
        RunbookRunId: string;
    }>;
};
export type KubernetesDetailsFilter = {
    name?: string;
    namespace?: string;
    targetName?: string;
    kind?: string;
    status?: string;
};
export const hasNoFilters = (filter: KubernetesDetailsFilter) => Object.values(filter).every((val) => val === undefined || StringHelper.isNullOrWhiteSpace(val));
export const clusterScopedNamespaceFilter = "Cluster-scoped";
export const unknownStatusFilter = "Unknown";
const styles = {
    toggleButtonContainer: css({
        display: "flex",
        flexDirection: "row",
        padding: space[4],
        background: themeTokens.color.background.tertiary,
        borderRadius: borderRadius.medium,
        gap: space[4],
    }),
    filterHeader: css({
        alignItems: "center",
    }),
    actionsContainer: css({
        display: "flex",
        flexDirection: "row",
        gap: space[16],
        alignItems: "center",
    }),
    diffActionsContainer: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    }),
    tabContents: css({
        display: "contents",
    }),
    tabContentsHidden: css({
        display: "none",
    }),
};
export type KubernetesDetailsStepView = "Manifests" | "Snapshot";
export const KubernetesDetailsLayout = (props: KubernetesDetailsProps) => {
    const taskId = props.task.Id;
    const isCompleted = props.task.IsCompleted;
    const deploymentId = props.task.Arguments.DeploymentId;
    const runbookRunId = props.task.Arguments.RunbookRunId;
    const { activeView, setActiveView, ...rest } = props;
    const [filter, setFilter] = useState<KubernetesDetailsFilter>({});
    const [hasManifestSummaries, setHasManifestSummaries] = useState<boolean>(false);
    const isDiffsFeatureEnabled = useKubernetesManifestInspectionDiffs();
    const [showDiffs, setShowDiffs] = useState<boolean>(false);
    const [diffViewTypeInStorage, setDiffViewTypeInStorage] = useLocalStorage<DiffViewType>("Octopus.Kubernetes.ManifestDiffs.DiffViewType", "split");
    const [diffSettings, setDiffSettings] = useState<DiffSettings>({ viewType: diffViewTypeInStorage, hideUnchangedManifests: false });
    const [diffSelection, setDiffSelection] = useState<DeploymentDiffSelection>({ fromDeploymentId: undefined, toDeploymentId: deploymentId });
    const viewKubernetesAppliedManifestsDispatcher = useAnalyticViewKubernetesAppliedManifestDispatch();
    const viewKubernetesObjectDrawerDispatcher = useAnalyticViewKubernetesObjectDrawerDispatch();
    const [isLoadingTimeline, timelineLoadingError, projectDeploymentTimeline] = useTimelineItems(props.projectId, props.environmentId, props.task?.Arguments.DeploymentId ?? props.task?.Arguments.RunbookRunId, props.tenantId);
    const priorDeploymentTimelineItems = useMemo(() => {
        if (projectDeploymentTimeline !== undefined) {
            return {
                items: projectDeploymentTimeline.items.filter((item) => (!item.IsHistorical && item.Created ? item.Created < projectDeploymentTimeline.currentItem.Created : false)),
                currentItem: projectDeploymentTimeline.currentItem,
            };
        }
        return undefined;
    }, [projectDeploymentTimeline]);
    useEffect(() => {
        viewKubernetesAppliedManifestsDispatcher("View Applied Manifests", {
            deploymentId: props.task?.Arguments.DeploymentId ?? props.task?.Arguments.RunbookRunId ?? "unknown",
            releaseId: props.snapshotId ?? "unknown",
            environmentId: props.environmentId ?? "unknown",
            projectId: props.projectId ?? "unknown",
            view: props.activeView,
            deploymentStatus: props.task.State.toString(),
            executionType: props.task?.Arguments.DeploymentId ? "deployment" : "runbook",
        }); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.activeView]);
    const { result: queryResult, isLoading: isLoadingManifests, error, } = useInlineStatusQuery(async (repo) => {
        const summaries = () => {
            if (deploymentId) {
                return repo.KubernetesManifest.getKubernetesManifestSummariesForDeployment(deploymentId);
            }
            else if (runbookRunId) {
                return repo.KubernetesManifest.getKubernetesManifestSummariesForRunbookRun(runbookRunId);
            }
            return Promise.reject("Either deploymentId or runbookRunId must be set.");
        };
        const resourceStatus = repo.Tasks.retrieveKubernetesStatusUpdate(taskId);
        return {
            summaries: await summaries(),
            resourceStatus: await resourceStatus,
        };
    }, [deploymentId, runbookRunId, taskId], "Kubernetes Object summary and statuses", {
        refetchIntervalInMs: isCompleted ? undefined : 5000, // refetch every 5s (unless we are done)
    });
    //if we don't have any manifest summaries, but the query result has some, set it to true
    //this will be used to enable the applied manifests view/button
    if (!hasManifestSummaries && (queryResult?.summaries.Resources.length || 0) > 0) {
        setHasManifestSummaries(true);
    }
    const diffsResult = useInlineStatusQuery(async (repo) => {
        if (!showDiffs || !diffSelection?.fromDeploymentId) {
            return null;
        }
        return repo.KubernetesManifest.getKubernetesManifestsDiffsForDeployment(diffSelection.fromDeploymentId, diffSelection.toDeploymentId);
    }, [showDiffs, diffSelection?.fromDeploymentId, diffSelection?.toDeploymentId], "Kubernetes Manifest Diffs");
    const diffQueryResult: DiffQueryResult | undefined = useMemo(() => ({
        ...diffsResult,
        selection: diffSelection,
    }), [diffSelection, diffsResult]);
    const errorInfo: ErrorInfo | undefined = useMemo(() => createErrorInfo(error), [error]);
    const viewToggleButtons = useMemo(() => {
        // conditionally wraps the child component in a tooltip
        const wrapInTooltip = (children: ReactNode, tooltipText: string, wrap: boolean) => (wrap ? <Tooltip content={tooltipText}>{children}</Tooltip> : children);
        const tooltipMsg = props.task.IsCompleted ? "Manifests aren't available for this deployment" : "Manifests unavailable until deployment completed";
        const updateDiffSettings = (updated: Partial<DiffSettings>) => {
            setDiffSettings((prev) => {
                const updatedSettings = {
                    ...prev,
                    ...updated,
                };
                setDiffViewTypeInStorage(updatedSettings.viewType);
                return updatedSettings;
            });
        };
        const showDiffComponents = isDiffsFeatureEnabled && deploymentId !== undefined && isCompleted;
        const disableDiffComponents = !!timelineLoadingError || !priorDeploymentTimelineItems?.items.length;
        const diffComponentsToolTipMsg = timelineLoadingError ? `Diffs unavailable due to ${timelineLoadingError.message}` : priorDeploymentTimelineItems?.items.length ? "Show diffs between deployments" : "No prior deployments available for diffing";
        return [
            <div className={styles.actionsContainer}>
                {props.activeView === "Manifests" &&
                    showDiffComponents &&
                    wrapInTooltip(<div className={styles.diffActionsContainer}>
                            {/* The Switch has an existing 16px right margin, so we surround these in the div so we don't get the extra flex gap */}
                            <Switch value={showDiffs} onChange={setShowDiffs} label="Show diffs" disabled={disableDiffComponents}/>
                            <DiffConfigMenu updateDiffSettings={updateDiffSettings} diffSettings={diffSettings}/>
                        </div>, diffComponentsToolTipMsg, disableDiffComponents)}
                <div className={styles.toggleButtonContainer}>
                    {wrapInTooltip(<Button label={"Applied Manifests"} icon={<CodeIcon size={20}/>} importance={activeView === "Manifests" ? "loud" : "quiet"} onClick={() => setActiveView("Manifests")} disabled={!!errorInfo || !hasManifestSummaries}/>, tooltipMsg, !hasManifestSummaries //no manifest summaries, disable the button and wrap in tooltip
                )}
                    <Button label={"Object Snapshot"} icon={<KubernetesIcon size={20}/>} importance={activeView === "Snapshot" ? "loud" : "quiet"} onClick={() => setActiveView("Snapshot")} disabled={!!errorInfo}/>
                </div>
            </div>,
        ];
    }, [
        props.task.IsCompleted,
        props.activeView,
        timelineLoadingError,
        priorDeploymentTimelineItems?.items.length,
        isDiffsFeatureEnabled,
        deploymentId,
        isCompleted,
        showDiffs,
        diffSettings,
        activeView,
        errorInfo,
        hasManifestSummaries,
        setDiffViewTypeInStorage,
        setActiveView,
    ]);
    const updateFilter = (changedFilters: Partial<KubernetesDetailsFilter>) => setFilter((prev) => ({
        ...prev,
        ...changedFilters,
    }));
    const filterSections: FilterSection[] = useMemo(() => {
        //any error? no filters
        if (errorInfo) {
            return [];
        }
        const filters: React.ReactNode[] = [];
        const statuses = queryResult?.resourceStatus.KubernetesStepsStatus || [];
        const manifestSummaries = queryResult?.summaries.Resources || [];
        const diffResources = flattenDiffsResourceHierarchy(diffQueryResult?.result?.Resources);
        const distinctFilters = (kosMap: (kos: KubernetesObjectStatus) => string, manifestSummariesMap: (krms: KubernetesResourceManifestSummaryResource) => string): Item[] => {
            const kosValues = statuses.flatMap((kss) => kss.KubernetesObjects.map(kosMap));
            const summaryValues = manifestSummaries.map(manifestSummariesMap);
            const removedDiffSummaryValues = diffQueryResult
                ? diffResources
                    .map((diffResource) => (diffQueryResult.selection.fromDeploymentId ? getManifestSummaryForDeployment(diffResource, diffQueryResult.selection.fromDeploymentId) : undefined))
                    .filter((r) => !!r)
                    .map(manifestSummariesMap)
                : [];
            return kosValues
                .concat(summaryValues)
                .concat(removedDiffSummaryValues)
                .filter((value, idx, arr) => arr.findIndex((x) => x === value) === idx)
                .map((x) => ({ value: x, text: x }));
        };
        const targetNames = distinctFilters((kos) => kos.ClusterName, (krms) => krms.MachineName);
        if (targetNames.length > 1) {
            filters.push(<Select key="targetNamesFilter" value={filter.targetName} onChange={(targetName) => updateFilter({ targetName })} items={targetNames} allowClear={true} placeholder="By deployment target"/>);
        }
        const namespaceNames = distinctFilters((kos) => kos.Namespace || clusterScopedNamespaceFilter, (krms) => krms.KubernetesObjectNamespace || clusterScopedNamespaceFilter);
        if (namespaceNames.length > 1) {
            filters.push(<Select key="namespaceFilter" value={filter.namespace} onChange={(namespace) => updateFilter({ namespace })} items={namespaceNames} allowClear={true} placeholder="By namespace"/>);
        }
        const kinds = distinctFilters((kos) => kos.Kind, (krms) => krms.KubernetesResourceKind);
        if (kinds.length > 1) {
            filters.push(<Select key="kindFilter" value={filter.kind} onChange={(kind) => updateFilter({ kind })} items={kinds} allowClear={true} placeholder="By object kind"/>);
        }
        const statusFilterItems = distinctFilters((kos) => kos.Status, (krms) => krms.ResourceStatus || unknownStatusFilter);
        if (statusFilterItems.length > 1) {
            filters.push(<Select key="statusFilter" value={filter.status} onChange={(status) => updateFilter({ status })} items={statusFilterItems} allowClear={true} placeholder="By object status"/>);
        }
        //if we have no advanced filters, don't show anything
        if (filters.length === 0) {
            return [];
        }
        return [
            {
                render: <div>{filters}</div>,
            },
        ];
    }, [errorInfo, queryResult?.resourceStatus.KubernetesStepsStatus, queryResult?.summaries.Resources, diffQueryResult, filter.targetName, filter.namespace, filter.kind, filter.status]);
    const isLoading = isLoadingManifests || isLoadingTimeline;
    return (<AdvancedFilterLayout filterSections={filterSections} additionalHeaderFilters={errorInfo ? undefined : [<FilterSearchBox placeholder="Search objects..." value={filter.name} onChange={(name: string) => updateFilter({ name })}/>]} filter={filter} defaultFilter={{}} onFilterReset={() => setFilter({})} additionalHeaderControls={viewToggleButtons} filterHeaderCustomStyle={styles.filterHeader} extendContentToEdges={true} renderContent={() => (<>
                    {errorInfo && <ErrorPanel error={errorInfo}/>}
                    {isLoading && <KubernetesDetailsLoadingSkeleton activeView={activeView}/>}
                    {!errorInfo && !isLoading && (<>
                            <div className={activeView === "Snapshot" ? styles.tabContents : styles.tabContentsHidden}>
                                <KubernetesSnapshotStepsLayout {...rest} filter={filter} taskState={props.task.State} resourceStatusResult={queryResult?.resourceStatus} onDrawerOpen={(object: KubernetesObjectStatus) => viewKubernetesObjectDrawerDispatcher("View Kubernetes Object Tab", {
                    liveApplicationHealth: "unknown",
                    objectHealth: object.Status,
                    objectId: "unknown",
                    objectType: object.Kind,
                    parentPage: "KOS",
                    tab: "Applied Manifest",
                    deploymentId: props.task?.Arguments.DeploymentId ?? props.task?.Arguments.RunbookRunId ?? "unknown",
                    releaseId: props.snapshotId ?? "unknown",
                    environmentId: props.environmentId ?? "unknown",
                    projectId: props.projectId ?? "unknown",
                    deploymentStatus: props.task.State.toString(),
                    executionType: props.task?.Arguments.DeploymentId ? "deployment" : "runbook",
                })}/>
                            </div>
                            <div className={activeView === "Manifests" ? styles.tabContents : styles.tabContentsHidden}>
                                <KubernetesAppliedManifests {...rest} filter={filter} executionId={deploymentId || runbookRunId} summariesResult={queryResult?.summaries} resourceStatusResult={queryResult?.resourceStatus} showDiffs={showDiffs} diffSettings={diffSettings} diffsQuery={diffQueryResult} diffSelectionChanged={(newSelection) => setDiffSelection(newSelection)} priorTimelineItems={priorDeploymentTimelineItems}/>
                            </div>
                        </>)}
                </>)}/>);
};
